import React, { useEffect, useMemo, useState } from "react";
import Box from "../../primitives/Box/Box";
import { Body3RegularTypography } from "../../primitives/Typography/typographyVariants";
import { ColorKey } from "../../theme/theme.types";

const DEFAULT_INTERVAL_MS = 2000;
const TRANSITION_MS = 450;
const LINE_HEIGHT_PX = 21;
const KEYWORD_TRANSITION = "transform 0.45s cubic-bezier(0.4, 0, 0.2, 1)";
const OPACITY_TRANSITION = "opacity 0.45s cubic-bezier(0.4, 0, 0.2, 1)";

type SearchInputAnimatedKeywordsProps = {
  keywords: string[];
  intervalMs?: number;
  color?: ColorKey;
};

export const SearchInputAnimatedKeywords: React.FC<
  SearchInputAnimatedKeywordsProps
> = ({
  keywords,
  intervalMs = DEFAULT_INTERVAL_MS,
  color = "text.default.secondary",
}) => {
  const safeKeywords = useMemo(() => keywords.filter(Boolean), [keywords]);
  const loopKeywords = useMemo(
    () =>
      safeKeywords.length > 1
        ? [...safeKeywords, safeKeywords[0]]
        : safeKeywords,
    [safeKeywords]
  );
  const [index, setIndex] = useState(0);
  const [animate, setAnimate] = useState(true);
  const prefersReducedMotion = useMemo(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    []
  );

  useEffect(() => {
    if (safeKeywords.length <= 1 || prefersReducedMotion) return undefined;

    const timer = window.setInterval(() => {
      setAnimate(true);
      setIndex((prev) => {
        const next = prev + 1;
        return next > safeKeywords.length ? prev : next;
      });
    }, intervalMs);

    return () => window.clearInterval(timer);
  }, [safeKeywords.length, intervalMs, prefersReducedMotion]);

  useEffect(() => {
    if (index !== safeKeywords.length || safeKeywords.length <= 1) {
      return undefined;
    }

    const resetTimer = window.setTimeout(() => {
      setAnimate(false);
      setIndex(0);
    }, TRANSITION_MS);

    return () => window.clearTimeout(resetTimer);
  }, [index, safeKeywords.length]);

  if (safeKeywords.length === 0) return null;

  const shouldAnimate = animate && !prefersReducedMotion;

  return (
    <Box
      as="span"
      aria-hidden
      display="inline-block"
      height={LINE_HEIGHT_PX}
      overflow="hidden"
      style={{ verticalAlign: "top" }}
    >
      <Box
        as="span"
        display="block"
        transition={shouldAnimate ? KEYWORD_TRANSITION : "none"}
        style={{ transform: `translateY(-${index * LINE_HEIGHT_PX}px)` }}
      >
        {loopKeywords.map((keyword, idx) => (
          <Body3RegularTypography
            key={`${keyword}-${idx}`}
            as="span"
            display="block"
            height={LINE_HEIGHT_PX}
            lineHeight={LINE_HEIGHT_PX}
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
            color={color}
            opacity={shouldAnimate && idx === index - 1 ? 0.2 : 1}
            transition={shouldAnimate ? OPACITY_TRANSITION : "none"}
          >
            {keyword}
          </Body3RegularTypography>
        ))}
      </Box>
    </Box>
  );
};

export default SearchInputAnimatedKeywords;
